// THE HAAL PROJECT — IRL LENS EXPANDED DATA
// Seasonal Lens · Ayat Bridge · Ward IRL Encounters · Deep Dive · Submissions
// Haqq · Amanah · Islah — fisabilillah

export interface AyatBridgeEntry {
  ar: string;
  en: string;
  root: string;
  rootM: string;
  ayat: string[];
  processes: string[];
  encounters: string[];
  field: string;
  sim?: string;
  m?: string;
  cat?: string;
  procs?: string[];
}

export interface SeasonalAttr {
  ar: string;
  en: string;
  m: string;
  role: string;
}

export interface SeasonalData {
  name: string;
  ar: string;
  icon: string;
  note: string;
  attrs: SeasonalAttr[];
  ayah: string;
  observe: string[];
}

export interface WardEncounterAttr {
  ar: string;
  en: string;
  m: string;
  role: string;
}

export interface WardEncounterSunnat {
  process: string;
  sci: string;
  attrs: WardEncounterAttr[];
  ayah: string;
}

export interface WardEncounter {
  id: string;
  title: string;
  icon: string;
  zahir: {
    desc: string;
    obs: string[];
  };
  threeQ: {
    is: string;
    what: string;
    when: string;
  };
  sunnat: WardEncounterSunnat[];
  haqq: string;
  amanah: string;
  uboor: string;
  bridges: string[];
  catalog: string;
}

export interface CaretakerSubmission {
  id: string;
  type: 'observation' | 'resource';
  cityId: string;
  wardId: string;
  wardName: string;
  title: string;
  description: string;
  category?: string;
  address?: string;
  threeQuestions?: { is: string; what: string; when: string };
  namesAttributes: string[];
  status: 'pending' | 'approved';
  createdAt: string;
}

// ─── AYAT BRIDGE — Names as Vectors ──────────────────────────────────────────

export const AYAT_BRIDGE: Record<string, AyatBridgeEntry> = {
  'Al-Muhyi': {
    ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a',
    en: 'The Life-Giver',
    root: 'h-y-y',
    rootM: 'to live, to give life',
    ayat: [
      'And you see the earth barren, but when We send down upon it rain, it quivers and swells and grows every beautiful kind. (22:5)',
      'It is He who gives life and causes death, and to Him you will be returned. (10:56)',
      'How can you disbelieve in Allah when you were lifeless and He brought you to life? (2:28)',
    ],
    processes: [
      'Ecological succession on vacant lots',
      'Seed germination after dormancy',
      'Urban agriculture on abandoned land',
      'Spring resurrection of dormant ecosystems',
      'Community restoration after displacement',
    ],
    encounters: ['vacant_prairie', 'brightmoor_farm', 'homewood_farms', 'garfield_conservatory'],
    field: 'Where do you see life emerging from apparent death? A crack in concrete with a plant. A vacant lot returning to prairie. A community rebuilding after displacement. Al-Muhyi gives life — document where you witness this giving.',
  },
  'Ar-Razzaq': {
    ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642',
    en: 'The All-Provider',
    root: 'r-z-q',
    rootM: 'to provide sustenance',
    ayat: [
      'Indeed, it is Allah who is the Provider, the firm possessor of strength. (51:58)',
      'And there is no creature on earth but that upon Allah is its provision. (11:6)',
      'And in the heaven is your provision and whatever you are promised. (51:22)',
    ],
    processes: [
      'Halal food corridor development',
      'Community food sovereignty',
      'Urban farming yields',
      'Mutual aid networks',
      'Provision through entrepreneurship',
    ],
    encounters: ['jos_campau', 'brightmoor_farm', 'iman_cafe', 'iman_atl_campus'],
    field: 'Where is provision flowing in your community? A halal market. A food bank. A community garden. A neighbor sharing. Track the channels through which Ar-Razzaq provides.',
  },
  'Al-Haqq': {
    ar: '\u0627\u0644\u0652\u062d\u064e\u0642\u0651',
    en: 'The True Reality',
    root: 'h-q-q',
    rootM: 'to be true, real, right',
    ayat: [
      'That is because Allah is the True Reality, and that what they call upon other than Him is falsehood. (22:62)',
      'And say: Truth has come and falsehood has departed. Indeed, falsehood is ever bound to depart. (17:81)',
      'We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the truth. (41:53)',
    ],
    processes: [
      'Gentrification mechanisms exposed',
      'Infrastructure violence truth-telling',
      'Displacement accounting',
      'Demographic shift documentation',
    ],
    encounters: ['sears_void', 'wg_hospital_gap', 'lakefront_67', 'otr_facade', 'hayti_freeway', 'alberta_st', 'civic_void'],
    field: 'Where do you see the gap between narrative and reality? A revitalized neighborhood where original residents cannot afford to live. A highway called progress that demolished a community. Document the Haqq.',
  },
  'Ash-Shahid': {
    ar: '\u0627\u0644\u0634\u064e\u0651\u0647\u0650\u064a\u062f',
    en: 'The Witness',
    root: 'sh-h-d',
    rootM: 'to witness, to testify',
    ayat: [
      'Allah is Witness over all things. (58:6)',
      'And Allah is sufficient as a Witness. (4:79)',
      'We have not neglected in the Register a thing. (6:38)',
    ],
    processes: [
      'Community documentation',
      'IRL Lens observation',
      'Bearing witness to displacement',
      'Photography as testimony',
      'Oral history preservation',
    ],
    encounters: ['community_masjid_atl', 'by_musalla', 'breonna_site', 'august_wilson', 'vanport_memorial'],
    field: 'What needs witnessing in your community? An injustice undocumented. A community unrecorded. A story untold. Ash-Shahid witnesses all — the Caretaker bears witness with the IRL Lens.',
  },
  'Al-Jami': {
    ar: '\u0627\u0644\u0652\u062c\u064e\u0627\u0645\u0650\u0639',
    en: 'The Gatherer',
    root: 'j-m-a',
    rootM: 'to gather, to collect, to unite',
    ayat: [
      'And hold firmly to the rope of Allah all together and do not become divided. (3:103)',
      'Our Lord, surely You will gather the people for a Day about which there is no doubt. (3:9)',
    ],
    processes: [
      'Refugee community formation',
      'Block club organizing',
      'Interfaith coexistence',
      'Iftar as community gathering',
      'Masjid as anchor institution',
    ],
    encounters: ['by_musalla', 'community_masjid_atl', 'iman_cafe', 'interfaith_walk', 'bosnian_center', 'iroquois_park'],
    field: 'Where do you see gathering in your community? A masjid overflowing for Jumu\u02bfah. A block club meeting. An iftar crossing ethnic lines. Al-Jami gathers — document where community forms.',
  },
  'As-Sabur': {
    ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631',
    en: 'The Patient',
    root: 's-b-r',
    rootM: 'to be patient, to endure',
    ayat: [
      'Indeed, Allah is with the patient. (2:153)',
      'And be patient, for indeed, Allah does not allow to be lost the reward of those who do good. (11:115)',
    ],
    processes: [
      'Generational community organizing',
      'Institutional persistence through crisis',
      'Masjid endurance amid vacancy',
      'Homeowner stewardship despite devaluation',
    ],
    encounters: ['community_masjid_atl', 'garfield_conservatory', 'gratiot_masjid', 'masjid_saber', 'prichill_stairs', 'homewood_ave'],
    field: 'Where do you see patience manifest? A masjid that survived decades of disinvestment. A homeowner maintaining their property through decline. A community organizing for the 10th year. Document the patience.',
  },
  'Al-Adl': {
    ar: '\u0627\u0644\u0652\u0639\u064e\u0624\u0650\u064a\u0645',
    en: 'The Just',
    root: 'a-d-l',
    rootM: 'to be just, to be equitable',
    ayat: [
      'Indeed, Allah commands justice and good conduct. (16:90)',
      'O you who believe, be persistently standing firm in justice. (4:135)',
    ],
    processes: [
      'Structural segregation exposure',
      'Life expectancy gap measurement',
      'Transit equity analysis',
      'Food desert mapping',
      'Infrastructure violence documentation',
    ],
    encounters: ['wg_hospital_gap', 'lakefront_67', 'sears_void', '9th_street', 'civic_void'],
    field: 'Where do you see the gap between what is and what should be? Measure it. Document it. The gap between the current state and Al-Adl\u2019s standard IS the Ayah.',
  },
  'Al-Baqi': {
    ar: '\u0627\u0644\u0652\u0628\u064e\u0627\u0642\u0650\u064a',
    en: 'The Everlasting',
    root: 'b-q-y',
    rootM: 'to remain, to endure, to be everlasting',
    ayat: [
      'What is with you will end, but what is with Allah will endure. (16:96)',
      'Everything upon the earth will perish, and there will remain the Face of your Lord. (55:26-27)',
    ],
    processes: [
      'Institutional persistence of masajid',
      'HBCU endurance through generations',
      'Community anchor survival',
      'Legacy homeowner stewardship',
    ],
    encounters: ['community_masjid_atl', 'iman_cafe', 'ams_oldest', 'gratiot_masjid', 'edgar_thomson', 'homewood_ave'],
    field: 'What endures in your community when everything around it changes? A masjid. A school. A family home. These are signs of Al-Baqi — document what remains.',
  },
  'Ash-Shafi': {
    ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a',
    en: 'The Healer',
    root: 'sh-f-y',
    rootM: 'to heal, to cure',
    ayat: [
      'And when I am ill, it is He who cures me. (26:80)',
      'O mankind, there has come to you instruction from your Lord and healing for what is in the breasts. (10:57)',
    ],
    processes: [
      'Community health clinic provision',
      'Healthcare desert documentation',
      'Healing through adjacent institutions',
      'Musalla and recovery center coexistence',
    ],
    encounters: ['wg_hospital_gap', 'icgc_minaret', 'prichill_musalla'],
    field: 'Where is healing present or absent in your community? A free clinic. A hospital that serves the uninsured. A masjid health fair. Document where Ash-Shafi manifests through human hands.',
  },
  'Al-Mujib': {
    ar: '\u0627\u0644\u0652\u0645\u064f\u062c\u0650\u064a\u0628',
    en: 'The Responsive One',
    root: 'j-w-b',
    rootM: 'to respond, to answer',
    ayat: [
      'And when My servants ask you concerning Me, indeed I am near. I respond to the invocation of every supplicant when they call upon Me. (2:186)',
    ],
    processes: [
      'Institutional responsiveness to community need',
      'Masjid as responsive anchor',
      'Community health fairs filling institutional gaps',
      'Mutual aid as divine response',
    ],
    encounters: ['icgc_minaret'],
    field: 'When the community calls, who responds? Document the institutions that respond and those that do not. Al-Mujib manifests through responsive institutions.',
  },
  'Al-Hakam': {
    ar: '\u0627\u0644\u0652\u062d\u064e\u0643\u064e\u0645',
    en: 'The Judge',
    root: 'h-k-m',
    rootM: 'to judge, to govern with wisdom',
    ayat: [
      'Allah commands you to render trusts to whom they are due. (4:58)',
      'Be persistently standing firm in justice, witnesses for Allah. (4:135)',
    ],
    processes: [
      'Muslim civic governance as Amanah not ownership',
      'Community benefit agreements',
      'Measuring revitalization by community wellbeing',
      'Justice for all residents regardless of background',
    ],
    encounters: ['hamtramck_council', 'otr_facade', 'vanport_memorial'],
    field: 'By what standard is governance measured — property values or community wellbeing? Document where Al-Hakam\u2019s standard of justice is applied and where it is absent.',
  },
  'Al-Malik': {
    ar: '\u0627\u0644\u0652\u0645\u064e\u0644\u0650\u0643',
    en: 'The Sovereign',
    root: 'm-l-k',
    rootM: 'to possess, to reign',
    ayat: [
      'Say: O Allah, Owner of Sovereignty, You give sovereignty to whom You will. (3:26)',
    ],
    processes: [
      'Human governance as Amanah not ownership',
      'Muslim civic participation reaching governance',
      'Community self-determination',
    ],
    encounters: ['hamtramck_council'],
    field: 'Where do you see governance exercised as Amanah — as trust — rather than as ownership? Document the difference between leaders who serve and those who rule.',
  },
  'Al-Muqsit': {
    ar: '\u0627\u0644\u0652\u0645\u064f\u0642\u0652\u0633\u0650\u0637',
    en: 'The Equitable',
    root: 'q-s-t',
    rootM: 'to be equitable, to act justly',
    ayat: [
      'Indeed, Allah loves those who act justly. (60:8)',
      'Woe to those who give less than due. (83:1)',
    ],
    processes: [
      'Life expectancy gap across neighborhood boundaries',
      'Cultural displacement without equity',
      'Revitalization measured by who benefits',
    ],
    encounters: ['9th_street', 'alberta_st'],
    field: 'Who benefits from development? Who is displaced? Al-Muqsit demands equity be measured, not assumed. Document the gap between stated equity goals and lived outcomes.',
  },
  'Al-Qarib': {
    ar: '\u0627\u0644\u0652\u0642\u064e\u0631\u0650\u064a\u0628',
    en: 'The Near One',
    root: 'q-r-b',
    rootM: 'to be near, to be close',
    ayat: [
      'And We are closer to him than his jugular vein. (50:16)',
      'Indeed I am near. I respond to the invocation of every supplicant. (2:186)',
    ],
    processes: [
      'Institutional adjacency paradox',
      'Physical proximity without access',
      'Masjid closing the distance between institution and community',
    ],
    encounters: ['icgc_minaret', 'wg_hospital_gap'],
    field: 'Proximity is not access. A world-class hospital next door does not mean the community is served. Document where Al-Qarib\u2019s nearness is reflected in institutional access — and where it is not.',
  },
  'Al-Warith': {
    ar: '\u0627\u0644\u0652\u0648\u064e\u0627\u0631\u0650\u062b',
    en: 'The Inheritor',
    root: 'w-r-th',
    rootM: 'to inherit, to be the heir',
    ayat: [
      'And indeed, it is We who give life and cause death, and We are the Inheritor. (15:23)',
      'What is with you will end, but what is with Allah will endure. (16:96)',
    ],
    processes: [
      'Nutrient cycling in autumn leaf fall',
      'Community inheritance of institutional legacy',
      'Cultural transmission across generations',
    ],
    encounters: ['vacant_prairie', 'ams_oldest'],
    field: 'What is being inherited in your community? Cultural knowledge. Institutional legacy. The nutrients of fallen leaves. Document what passes from one generation to the next.',
  },
  'Al-Wakil': {
    ar: '\u0627\u0644\u0652\u0648\u064e\u0643\u0650\u064a\u0644',
    en: 'The Trustee',
    root: 'w-k-l',
    rootM: 'to entrust, to rely upon',
    ayat: [
      'And put your trust in Allah. And sufficient is Allah as Trustee. (33:3)',
    ],
    processes: [
      'Generational institutional trusteeship',
      'Youth as Amanah entrusted to community',
      '85 years of masjid maintenance as trusteeship',
    ],
    encounters: ['ams_oldest', 'evanston_youth'],
    field: 'What has been entrusted to your community? A masjid. A school. A generation of children. Document how the community fulfills its role as trustee.',
  },
  'Al-Hafiz': {
    ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u0641\u0650\u0638',
    en: 'The Preserver',
    root: 'h-f-z',
    rootM: 'to preserve, to protect, to guard',
    ayat: [
      'Indeed, it is We who send down the Reminder and indeed, We will be its Guardian. (15:9)',
      'Let those fear who left behind weak offspring. (4:9)',
    ],
    processes: [
      'Mycorrhizal networks preserving root systems beneath snow',
      'Youth programs preserving childhood in poverty',
      'August Wilson preserving the Hill District in ten plays',
      'Institutional memory across generations',
    ],
    encounters: ['garfield_conservatory', 'evanston_youth', 'august_wilson'],
    field: 'What is being preserved in your community? A child\u2019s future. A cultural memory. A root system beneath the snow. Document the acts of preservation — human and natural.',
  },
};

// ─── CONTEXT BRIDGE — Historical Sites as IRL Lens Encounters ─────────────────

export const CTX_BRIDGE: Record<string, Record<string, string>> = {
  chicago: {
    englewood: 'IMAN\u2019s founding site, the former 63rd & Halsted commercial corridor, the vacant lots that were once a neighborhood of 97,000. Each block in Englewood carries Huduth Zamani readable through the IRL Lens.',
    'south-shore': 'Masjid Al-Fatir has served for decades from Stony Island. The Obama Presidential Center rises nearby. South Shore\u2019s timeline is written in its lakefront, its commercial corridors, its Muslim institutions.',
    'north-lawndale': 'Dr. King moved here in 1966. Sears departed in 1973. The Homan Square void is a classroom. Every historical marker in North Lawndale is an invitation to apply the Three Questions.',
    'west-garfield-park': 'Zero hospitals for 18,000 residents. Garfield Park Conservatory — curated life surrounded by disinvestment. Apply the IRL Lens to what the city preserves and what it abandons.',
    'back-of-the-yards': 'Storefront musallas serve Rohingya, Syrian, and Yemeni families. Two communities in crisis sharing space. Apply the Three Questions to the intersection of refugee resilience and urban disinvestment.',
  },
  atlanta: {
    'english-vine': 'IMAN\u2019s Green ReEntry site, the former Vine City commercial corridor, the vacant lots that were once a neighborhood of freedmen. Each block carries Huduth Zamani readable through the IRL Lens.',
    'west-end': 'Community Masjid of Atlanta has anchored four generations. The BeltLine Westside Trail triggers gentrification. Walk the grounds and apply the Three Questions to what endures and what is being displaced.',
  },
  cincinnati: {
    avondale: 'The Islamic Center has watched Avondale transform for 50 years. Children\u2019s Hospital expands while the community\u2019s health outcomes stagnate. Walk Reading Road with the IRL Lens and document the adjacency paradox.',
    'over-the-rhine': 'The 2001 uprising, the 3CDC transformation, the Italianate facades concealing displacement — Over-the-Rhine\u2019s built environment is a textbook of the Three Questions applied to gentrification.',
    'price-hill': 'Price Hill\u2019s storefront musallas sit beside recovery centers. Two communities in crisis sharing space. Apply the Three Questions to the intersection of refugee resilience and opioid devastation.',
    'walnut-hills': 'Peebles Corner was Black commerce. Now craft bars occupy legacy storefronts. Walk Peebles Corner with the IRL Lens.',
    'bond-hill': 'Reading Road through Bond Hill — former economic backbone. Islamic Academy visible on the corridor. Apply the IRL Lens to middle-class erosion.',
    roselawn: 'Islamic Center satellite, synagogue, and three churches share a half-mile stretch. Multiple communities of faith sharing space. Proximity is the sign.',
    northside: 'Community mural on Hamilton Ave depicting neighborhood history. New condos rising behind it. Art preserves community even as displacement gathers.',
    evanston: 'Youth center on Hewitt Ave. After-school programs where 45% of children live below poverty. Every child here is an Amanah.',
  },
  detroit: {
    'east-side': 'Detroit\u2019s East Side urban prairie is the IRL Lens at its most dramatic. Walk a vacant block where houses stood 15 years ago. Apply the Three Questions to tallgrass where bedrooms were. Nature\u2019s response to human abandonment is itself an Ayah of Al-Muhyi.',
    hamtramck: 'Hamtramck City Hall — first all-Muslim city council in America. Jos. Campau halal corridor. Apply the IRL Lens to Muslim civic governance and community food sovereignty.',
    'vernor-highway': 'American Moslem Society (est. 1938) — the oldest masjid in Michigan. Yemeni immigrants building community since Henry Ford\u2019s $5/day wage. Walk Vernor Highway with the IRL Lens.',
    brightmoor: 'Brightmoor Farmway — 10 urban farms on vacant lots. Food grows where houses stood. The Hadith made operational: plant even if the Hour is established.',
  },
  louisville: {
    'west-louisville': 'The 9th Street Divide. Stand at the line and apply the Three Questions. One street. Two life expectancies. 110 years of structural segregation readable in the built environment.',
    iroquois: 'Bosnian Islamic Center serves refugees from three waves of war. Iroquois Park — Olmsted-designed commons surrounded by the most ethnically diverse neighborhood in Kentucky. Apply the IRL Lens to intercultural gathering.',
  },
  durham: {
    hayti: 'The Durham Freeway cuts through what was Hayti — Black Wall Street of the South. Ar-Razzaq Islamic Center on S. Alston Ave. NCCU on the hill. Apply the Three Questions to the freeway trench.',
    'east-durham': 'Angier Ave through East Durham — the Triangle\u2019s immigrant and refugee corridor. Tiendas, halal markets, and Burmese restaurants. Where immigrants build new lives with minimal institutional support.',
  },
  portland: {
    northeast: 'Alberta Street was Black Portland. Apply the Three Questions to the arts district that displaced the community it celebrates. Vanport memorial nearby — two catastrophic displacements, 50 years apart.',
    'east-portland': '82nd Avenue — the displacement terminus. Halal markets, refugee restaurants, dollar stores. Masjid As-Saber: named The Patient in the displacement zone. The name is the Ayah.',
  },
  pittsburgh: {
    'hill-district': 'The Hill District — August Wilson\u2019s Pittsburgh. Civic Arena void. 8,000+ displaced. Apply the Three Questions to the 28-acre void and the community that persists above it.',
    homewood: 'Homewood\u2019s urban farms grow food on vacant lots where families once lived. 42% vacancy becomes agricultural potential. Apply the IRL Lens to community farming.',
    braddock: 'Braddock — Carnegie\u2019s first steel mill still operates with 800 workers vs 5,000+ at peak. The ghost economy is the IRL Lens encounter.',
  },
};

// ─── SEASONAL LENS ────────────────────────────────────────────────────────────

export const SEASONAL: Record<string, SeasonalData> = {
  winter: {
    name: 'Winter',
    ar: '\u0627\u0644\u0634\u062a\u0627\u0621',
    icon: '\u2744',
    note: 'In winter the trees reveal their structure — stripped to essential form, the Huduth Dhati of each branch is visible. Dormancy is not death but Al-Muakhkhir (The Delayer) holding life in reserve. The Caretaker notices: heating infrastructure, shelter access, who is outside in the cold.',
    attrs: [
      { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Winter teaches patience. Seeds wait beneath frozen ground by the Taqdir of As-Sabur.' },
      { ar: '\u0627\u0644\u0652\u0645\u064f\u0624\u064e\u062e\u0651\u0650\u0631', en: 'Al-Muakhkhir', m: 'The Delayer', role: 'Dormancy is divine delay — life held in reserve, not absent. Al-Muakhkhir delays the bloom until its appointed time.' },
      { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u0641\u0650\u0638', en: 'Al-Hafiz', m: 'The Preserver', role: 'Beneath snow, mycorrhizal networks preserve root systems. Al-Hafiz preserves through conditions that appear destructive.' },
    ],
    ayah: '"And it is He who sends the winds as good tidings before His mercy, and We send down from the sky pure water." (25:48)',
    observe: [
      'Bare tree architecture revealing root-to-crown structure',
      'Ice patterns on puddles — crystalline geometry of Al-Musawwir',
      'Breath vapor — the water cycle made visible in your own exhale',
      'Heating vents and who gathers near them',
      'Snow coverage patterns — where it melts first reveals underground infrastructure',
    ],
  },
  spring: {
    name: 'Spring',
    ar: '\u0627\u0644\u0631\u0628\u064a\u0639',
    icon: '\uD83C\uDF31',
    note: 'Spring is Al-Muhyi (The Life-Giver) made manifest. Every bud breaking, every seed germinating is a Kun (Be!) unfolding in real time. The Caretaker watches resurrection happen at the cellular level. Community gardens activate.',
    attrs: [
      { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Every bud that breaks open is Al-Muhyi\u2019s command executing. Spring is the annual demonstration of resurrection.' },
      { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u062d', en: 'Al-Fattah', m: 'The Opener', role: 'Al-Fattah opens what was closed. Buds open, soil thaws, pathways clear. The Caretaker watches opening happen at every scale.' },
      { ar: '\u0627\u0644\u0652\u0645\u064f\u0628\u0652\u062f\u0650\u0626', en: 'Al-Mubdi', m: 'The Originator', role: 'New growth from dormant branches. Al-Mubdi originates life from what appeared lifeless.' },
    ],
    ayah: '"And you see the earth barren, but when We send down upon it rain, it quivers and swells and grows something of every beautiful kind." (22:5)',
    observe: [
      'First buds on trees — time-lapse of Al-Muhyi\u2019s command',
      'Earthworms surfacing after rain — soil ecosystem activating',
      'Birdsong patterns changing — Al-Musawwir\u2019s seasonal design',
      'Community gardens being planted — human participation in divine provision',
      'Potholes filled with water — infrastructure gaps revealed by snowmelt',
    ],
  },
  summer: {
    name: 'Summer',
    ar: '\u0627\u0644\u0635\u064a\u0641',
    icon: '\u2600',
    note: 'Summer is peak Rizq manifestation — gardens produce, trees fruit, the longest days provide maximum light for provision. The heat also reveals disparity: who has cooling, who has shade, who has water access.',
    attrs: [
      { ar: '\u0627\u0644\u0646\u064f\u0651\u0648\u0631', en: 'An-Nur', m: 'The Light', role: 'Maximum daylight — An-Nur\u2019s provision at its annual peak. Photosynthesis runs at highest capacity.' },
      { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: 'Gardens produce, markets overflow, the earth gives its fruit. Ar-Razzaq\u2019s provision is visible everywhere.' },
      { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Summer storms demonstrate Al-Qawiyy\u2019s power. The Caretaker observes which communities have stormwater infrastructure and which flood.' },
    ],
    ayah: '"It is He who made the sun a shining light and the moon a derived light and determined for it phases." (10:5)',
    observe: [
      'Fruit ripening on neighborhood trees — catalog which species and where',
      'Heat island effect — temperature differences between concrete and green space',
      'Community gathering outdoors — the social ecology of summer',
      'Hydrant sprays in neighborhoods — children and water provision',
      'Vacant lots becoming prairie ecosystems — nature fills human absence',
    ],
  },
  autumn: {
    name: 'Autumn',
    ar: '\u0627\u0644\u062e\u0631\u064a\u0641',
    icon: '\uD83C\uDF42',
    note: 'Autumn is the great teacher of Al-Mumit (The Bringer of Death) working through Al-Muhyi. Leaves change, fall, become soil. The Caretaker watches the cycle complete. Harvest ends. Food deserts deepen as growing season closes.',
    attrs: [
      { ar: '\u0627\u0644\u0652\u0645\u064f\u0645\u0650\u064a\u062a', en: 'Al-Mumit', m: 'The Bringer of Death', role: 'Leaf fall is Al-Mumit\u2019s command — not destruction but transition. Death feeds life. The compost of autumn becomes the soil of spring.' },
      { ar: '\u0627\u0644\u0652\u0648\u064e\u0627\u0631\u0650\u062b', en: 'Al-Warith', m: 'The Inheritor', role: 'When the leaf falls, the tree inherits the nutrients back. Al-Warith ensures nothing is truly lost in the cycle.' },
      { ar: '\u0627\u0644\u0652\u0645\u064f\u0639\u0650\u064a\u062f', en: 'Al-Muid', m: 'The Restorer', role: 'The annual cycle is Al-Muid\u2019s promise: what decomposes will be restored. The Caretaker watches restoration at every scale.' },
    ],
    ayah: '"And among His signs is that you see the earth stilled, but when We send down upon it rain, it quivers and grows." (41:39)',
    observe: [
      'Leaf color change — chlorophyll giving way reveals carotenoids that were always present',
      'Seed dispersal patterns — each species has a unique Taqdir for spreading',
      'Squirrels caching nuts — provision planning as Sunnat Allah',
      'Harvest at community gardens — who gets the yield, how it circulates',
      'Shorter days shifting community rhythms — outdoor gatherings ending',
    ],
  },
};

export function getSeason(): 'spring' | 'summer' | 'autumn' | 'winter' {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'autumn';
  return 'winter';
}

// ─── DEEP DIVE ────────────────────────────────────────────────────────────────

export const DEEP_DIVE = {
  fission: {
    title: 'The Dunyah Fission Reactor',
    intro: 'The IRL Lens operates like a fission reactor: it takes a unified phenomenon (a tree, a building, a community) and splits it into its component divine elements — Names (Asma), Attributes (Sifat), and Commands (Amr). This mirrors nuclear fission where atoms split to release energy. Here, observation splits to release recognition.',
    diagram: 'PHENOMENON (Unified) \u2192 [Basiran/Tafakkur Neutrons] \u2192 SPLIT: Asma (Names) + Sifat (Attributes) + Amr (Commands) + Taqdir (Decree) + Ayah (Sign) \u2192 ENERGY RELEASE: Nur (Light of Recognition) + Hikmah (Wisdom) + Khidmah (Service)',
    components: [
      { name: 'Fuel Rods: Ayat al-Takwiniyyah', desc: 'The cosmic signs — every created thing is fuel for the reactor. A fruit tree, a fire hydrant, a community garden, a vacant lot — each is raw material for divine attribute recognition.' },
      { name: 'Tafakkur Neutrons', desc: 'Deep contemplative observation initiates the fission. When the Caretaker stops, looks, and asks "Is it? What is it? When is/was it?" — these questions are the neutrons that begin the split.' },
      { name: 'Moderators: Asma al-Husna', desc: 'The 99 Names regulate the reaction speed. Without them, the observation overwhelms. With them, each process unfolds at the pace of understanding.' },
      { name: 'Control Rods: Quranic Verses', desc: 'Ayat regulate the reaction — each verse illuminates a specific aspect, preventing spiritual meltdown (Ghaflah) and maintaining balance (Mizan).' },
      { name: 'Coolant: Barakah', desc: 'The blessed energy that prevents overheating. Dua, Dikr, and Sunnah practices circulate through the reactor, keeping the Caretaker grounded.' },
      { name: 'Output: Nur + Hikmah + Khidmah', desc: 'The energy released is threefold: Light of recognition (you see the Names), Wisdom (you understand their relationships), and Service (you act on the Amanah).' },
    ],
    ayah: '"We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the truth." (41:53)',
  },
  psychological: {
    title: 'The Hawas-to-Hikmah Pathway',
    intro: 'The psychological journey from raw sensory input to divine recognition follows a specific pathway through the Nafs. Understanding this pathway deepens the Caretaker\'s capacity to process what the Inner Reality Lens reveals.',
    stages: [
      { name: '1. Hawas (Senses)', desc: 'Raw sensory data enters: sight, sound, smell, taste, touch. This is the Zahir — the apparent surface.', attr: { ar: '\u0627\u0644\u0652\u0628\u064e\u0635\u0650\u064a\u0631', en: 'Al-Basir', m: 'The All-Seeing' }, process: 'Photons strike retina \u2192 neural signal \u2192 visual cortex \u2192 pattern recognition. Each step is an Amr executing.' },
      { name: '2. Aql (Intellect)', desc: 'The Aql ties sensory data to categories. "This is a tree." But the Aql also asks: what kind? How healthy? In what context?', attr: { ar: '\u0627\u0644\u0652\u0639\u064e\u0644\u0650\u064a\u0645', en: 'Al-Alim', m: 'The All-Knowing' }, process: 'Pattern matching \u2192 categorization \u2192 contextual analysis. The Aql functions as the IRL Lens processor.' },
      { name: '3. Fikr (Deep Thought)', desc: 'Fikr goes beneath the surface. The Three Questions activate here: Is it? What is it? When is/was it?', attr: { ar: '\u0627\u0644\u0652\u062d\u064e\u0643\u0650\u064a\u0645', en: 'Al-Hakim', m: 'The All-Wise' }, process: 'The slider of local causality: zoom in and it\'s an apple, zoom out and it\'s a tree, further and it\'s a seed, further still and it\'s a decree.' },
      { name: '4. Qalb (Heart)', desc: 'The Qalb is where recognition becomes felt. Not just "I see a tree" but "I witness a manifestation of Al-Khaliq."', attr: { ar: '\u0627\u0644\u0652\u0645\u064f\u0642\u064e\u0644\u0651\u0650\u0628', en: 'Al-Muqallib', m: 'The Turner of Hearts' }, process: 'Intellectual understanding \u2192 emotional resonance \u2192 spiritual recognition. The Qalb is the seat where Iman settles.' },
      { name: '5. Ruh (Spirit)', desc: 'The Ruh connects individual recognition to universal truth. The tree is not just this tree but all trees, all provision, all evidence of Ar-Razzaq.', attr: { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0650\u064a\u0637', en: 'Al-Muhit', m: 'The All-Encompassing' }, process: 'Individual observation \u2192 universal principle \u2192 Sunnat Allah recognition.' },
      { name: '6. Basirah (Insight)', desc: 'Basirah is sight beyond sight — seeing with the inner eye. The Caretaker now sees not just the tree but the Names operating through it.', attr: { ar: '\u0627\u0644\u0652\u062e\u064e\u0628\u0650\u064a\u0631', en: 'Al-Khabir', m: 'The All-Aware' }, process: 'Surface observation + depth processing + spiritual reception = Basirah. This is the IRL Lens fully activated.' },
      { name: '7. Hikmah (Wisdom)', desc: 'Hikmah is the fruit of the entire process — wisdom to act. The Caretaker does not just see and understand but knows what to do.', attr: { ar: '\u0627\u0644\u0652\u062d\u064e\u0643\u0650\u064a\u0645', en: 'Al-Hakim', m: 'The All-Wise' }, process: 'Recognition \u2192 Understanding \u2192 Amanah \u2192 Action. Hikmah is knowledge applied in service.' },
    ],
    ayah: '"He gives wisdom to whom He wills, and whoever has been given wisdom has certainly been given much good." (2:269)',
  },
  stem: {
    title: 'STEM as Sunnat Allah',
    intro: 'Every scientific process the Caretaker observes is a pattern of Sunnat Allah — the Way/System of Allah. STEM education becomes divine attribute recognition when the IRL Lens is applied.',
    processes: [
      {
        name: 'Photosynthesis (Chloroplast Reactor)',
        sci: '6CO\u2082 + 6H\u2082O + Light Energy \u2192 C\u2086H\u2081\u2082O\u2086 + 6O\u2082',
        detail: 'Each chloroplast is a microscopic fission reactor. Photon strikes chlorophyll \u2192 electron excites \u2192 electron transport chain \u2192 ATP synthesis \u2192 Calvin Cycle \u2192 glucose.',
        attrs: ['An-Nur (Light) \u2192 photon capture', 'Al-Muhyi (Life-Giver) \u2192 energy conversion', 'Ar-Razzaq (Provider) \u2192 sugar production', 'Al-Hakim (Wise) \u2192 efficient distribution'],
        ayah: '"Allah is the Light of the heavens and the earth." (24:35)',
        field: 'Observe a leaf in direct sunlight. The green color you see is the wavelength REJECTED by the chloroplast — the leaf absorbs red and blue for energy. The color of divine provision is invisible; what you see is the leftover.',
      },
      {
        name: 'Cellular Respiration (Mitochondrial Dhikr)',
        sci: 'C\u2086H\u2081\u2082O\u2086 + 6O\u2082 \u2192 6CO\u2082 + 6H\u2082O + ATP (Energy)',
        detail: 'Every living cell performs this — the reverse of photosynthesis. Your breathing is participation in the forest\'s dhikr cycle: you inhale their praise (O\u2082), exhale your need (CO\u2082).',
        attrs: ['Al-Muhyi (Life-Giver) \u2192 energy release', 'Al-Qayyum (Self-Sustaining) \u2192 continuous cellular operation', 'Al-Latif (Subtle) \u2192 molecular-level precision'],
        ayah: '"And there is no creature on earth but that upon Allah is its provision." (11:6)',
        field: 'Place your hand on your chest. Each heartbeat pumps blood carrying O\u2082 to mitochondria in every cell. 37 trillion cells performing respiration simultaneously. You are a walking demonstration of Al-Qayyum.',
      },
      {
        name: 'Water Cycle (Hydrological Tasbih)',
        sci: 'Evaporation \u2192 Condensation \u2192 Precipitation \u2192 Infiltration \u2192 Runoff \u2192 Collection',
        detail: 'Water molecules cycle between ocean, atmosphere, land, and organism. The same water that rained on the Prophet (peace be upon him) rains on you. The cycle is a perpetual tasbih — glorification through transformation.',
        attrs: ['Al-Hayy (Living) \u2192 water as medium of life', 'Al-Muqit (Sustainer) \u2192 continuous cycling', 'Al-Wahhab (Bestower) \u2192 rain as gift'],
        ayah: '"And We sent down from the sky water in a measured amount." (23:18)',
        field: 'After rain, watch puddle evaporation. The water you see disappearing is rising to become cloud, which will become rain, which will become river, which will become drink. Nothing is lost in Allah\'s system.',
      },
      {
        name: 'Soil Microbiome (Underground Ummah)',
        sci: 'Bacteria + Fungi + Archaea + Protozoa \u2192 Decomposition \u2192 Nutrient cycling \u2192 Plant growth',
        detail: 'One tablespoon of healthy soil contains more microorganisms than humans on Earth. Mycorrhizal fungal networks connect trees underground — sharing nutrients, sending chemical warnings. Scientists call it the Wood Wide Web.',
        attrs: ['Al-Jami (Gatherer) \u2192 mycorrhizal networks', 'Al-Batin (Hidden) \u2192 invisible underground systems', 'Al-Bari (Evolver) \u2192 decomposition into nutrients'],
        ayah: '"And in the earth are neighboring tracts and gardens of grapevines and crops and palm trees, growing from one root and others not from one root, watered with one water." (13:4)',
        field: 'Dig gently at the base of any tree. The white threads you see in the soil are mycorrhizal fungi — the physical network of Al-Jami connecting this tree to its neighbors. Step carefully; you are walking on a living network.',
      },
    ],
  },
};

// ─── WARD IRL ENCOUNTERS ──────────────────────────────────────────────────────

export const WARD_IRL: Record<string, Record<string, WardEncounter[]>> = {
  chicago: {
    englewood: [
      {
        id: 'iman_cafe',
        title: 'IMAN Arts Cafe & Campus',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'IMAN\'s campus near 63rd & Halsted in Englewood. The Arts Cafe, the health clinic, the green reentry workshop — all on a block that the city wrote off decades ago.',
          obs: [
            'Mural on the cafe wall depicting community',
            'Green ReEntry workshop with construction materials visible',
            'Health clinic entrance with multilingual signage',
            'Corner Garden maintained by reentry participants',
            '63rd Street corridor — some storefronts occupied, many vacant',
          ],
        },
        threeQ: {
          is: 'IMAN exists as a manifestation of "If the Hour is established and one has a palm shoot, let him plant it." It is clinic, cafe, workshop, shelter, and proof that Islah works.',
          what: 'A holistic ecosystem of care in a community where care was systematically withdrawn. The Huduth Dhati is Khidmah — service.',
          when: 'Founded 1997. Englewood had already lost 75% of its population. IMAN planted anyway. The cafe opened. The clinic opened. The Village housing opened. Each one an act of defiance against disinvestment.',
        },
        sunnat: [
          {
            process: 'Holistic Community Development',
            sci: 'Health access + arts expression + employment training + housing stability + spiritual grounding \u2192 community restoration',
            attrs: [
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: 'IMAN channels Rizq into a community where capital fled. The health clinic provides what the market abandoned.' },
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Englewood was declared dead by policymakers. IMAN practices resurrection daily.' },
              { ar: '\u0627\u0644\u062a\u064e\u0651\u0648\u064e\u0651\u0627\u0628', en: 'At-Tawwab', m: 'The Acceptor of Repentance', role: 'Every returning citizen who walks through IMAN\'s doors is met with the reality of At-Tawwab — that return and restoration are always possible.' },
            ],
            ayah: '"Whoever saves a life, it is as if they saved all of humanity." (5:32)',
          },
        ],
        haqq: 'IMAN is the Haqq made operational — truth, right, reality, and purpose converging in service.',
        amanah: 'The Amanah is to learn from IMAN\'s model and replicate it. Document how holistic care transforms indicators.',
        uboor: 'IMAN itself IS the bridge. This encounter connects to every resource category in the guide.',
        bridges: ['food', 'health', 'reentry', 'community'],
        catalog: 'Your documentation of IMAN\'s campus catalogs not just a location but a methodology — the living proof that Muslim-led holistic development works.',
      },
    ],
    'south-shore': [
      {
        id: 'lakefront_67',
        title: '67th Street Lakefront',
        icon: '\uD83C\uDF0A',
        zahir: {
          desc: 'Lake Michigan at 67th Street Beach. The same water that touches the Gold Coast downtown touches South Shore here — but the infrastructure tells a different story.',
          obs: [
            'Lake Michigan stretching to the horizon',
            'Beach with limited facilities compared to North Side beaches',
            'South Shore Cultural Center — a restored landmark',
            'Joggers on the lakefront trail',
          ],
        },
        threeQ: {
          is: 'This lake is the same body of water at every point of its shore. Yet its shoreline infrastructure varies by neighborhood wealth.',
          what: 'The Huduth Dhati of this water is Al-Hayy — the medium of all life. Its Huduth Zamani: South Shore was denied lakefront investment for decades.',
          when: 'Lake Michigan has cycled water since before human habitation. The beaches were segregated until the 1960s. The investment gap persists.',
        },
        sunnat: [
          {
            process: 'Water Cycle & Coastal Ecology',
            sci: 'Evaporation \u2192 cloud formation \u2192 precipitation \u2192 runoff \u2192 lake return',
            attrs: [
              { ar: '\u0627\u0644\u0652\u062d\u064e\u064a\u0651', en: 'Al-Hayy', m: 'The Ever-Living', role: 'Water is the medium of Al-Hayy. Every living thing was made from it. The lake sustains 10 million people.' },
              { ar: '\u0627\u0644\u0652\u0639\u064e\u062f\u0652\u0644', en: 'Al-Adl', m: 'The Just', role: 'The same water, different infrastructure. Al-Adl calls the Caretaker to measure the gap.' },
            ],
            ayah: '"And We sent down from the sky water in a measured amount and settled it in the earth." (23:18)',
          },
        ],
        haqq: 'The water does not discriminate. The systems that control access to it do.',
        amanah: 'Document the infrastructure gap. Advocate for equitable restoration.',
        uboor: 'Bridges to health resources, community spaces, and youth programs.',
        bridges: ['health', 'community'],
        catalog: 'Your lakefront documentation adds to the environmental justice map of South Shore.',
      },
    ],
    'north-lawndale': [
      {
        id: 'sears_void',
        title: 'The Homan Square Void',
        icon: '\u25A1',
        zahir: {
          desc: 'You stand at Homan and Arthington where Sears Roebuck once employed 10,000+ people. The void left by corporate departure is visible in every direction.',
          obs: [
            'Massive empty lots where the Sears complex stood',
            'Residential blocks with intermittent vacancy',
            'Lawndale Christian Health Center nearby',
            'Community murals depicting neighborhood history',
          ],
        },
        threeQ: {
          is: 'This void exists as absence — the negative space where 10,000 jobs once circulated wages through a community. The absence itself is an Ayah.',
          what: 'The Huduth Dhati of a void is potential — what was here, what could be here.',
          when: 'Sears built here in 1906. Peak employment: 10,000+. Departure: 1973. Dr. King moved here in 1966 to fight housing discrimination.',
        },
        sunnat: [
          {
            process: 'Economic Cascade Failure',
            sci: 'Anchor employer departs \u2192 local wages vanish \u2192 commercial corridor collapses \u2192 property values drop \u2192 services withdraw',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0627\u0628\u0650\u0636', en: 'Al-Qabid', m: 'The Constrictor', role: 'Al-Qabid constricts provision as a test and a sign. When Sears left, the constriction was not divine will alone but human decision.' },
              { ar: '\u0627\u0644\u0652\u0628\u064e\u0627\u0633\u0650\u0637', en: 'Al-Basit', m: 'The Expander', role: 'Al-Basit expands provision when conditions align. The Caretaker\'s role is to create the conditions for expansion — to plant in the void.' },
            ],
            ayah: '"Perhaps you hate a thing and it is good for you; and perhaps you love a thing and it is bad for you." (2:216)',
          },
        ],
        haqq: 'The void is not empty — it is full of potential. Every vacant lot in North Lawndale is a question: what will the Caretaker plant here?',
        amanah: 'Document the void and what grows in it. Every community garden, every mutual aid effort — catalog the Islah.',
        uboor: 'Bridges to employment, reentry, income, and community development resources.',
        bridges: ['employ', 'reentry', 'income', 'community'],
        catalog: 'Your documentation of this economic void and its emerging restoration enters the collective Lawndale record.',
      },
    ],
    'west-garfield-park': [
      {
        id: 'garfield_conservatory',
        title: 'Garfield Park Conservatory',
        icon: '\uD83C\uDF3F',
        zahir: {
          desc: 'One of the world\'s largest conservatories. Lush tropical plants inside. 75% population loss outside.',
          obs: [
            'Glass conservatory with tropical plants',
            'Surrounding blocks with 35% vacancy',
            'Children on field trips',
          ],
        },
        threeQ: {
          is: 'Curated life surrounded by disinvestment. Both are Ayat.',
          what: 'A preserved ecosystem within a struggling ecosystem.',
          when: 'Built 1907. Surrounding community peaked at 70,000. Now under 18,000.',
        },
        sunnat: [
          {
            process: 'Microclimate Ecology',
            sci: 'Glass enclosure \u2192 controlled temp \u2192 humidity \u2192 photosynthetic optimization',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Al-Muhyi creates tropical life in Chicago winter. Outside: the community needs the same investment.' },
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u0641\u0650\u0638', en: 'Al-Hafiz', m: 'The Preserver', role: 'Al-Hafiz preserves the plants. Who preserves the community?' },
            ],
            ayah: '"We caused to grow therein every thing well-balanced." (15:19)',
          },
        ],
        haqq: 'The conservatory proves that with investment, life flourishes anywhere.',
        amanah: 'Document the contrast between institutional preservation and community preservation.',
        uboor: 'Bridges to health and community resources.',
        bridges: ['health', 'community'],
        catalog: 'Your conservatory documentation maps the investment contrast.',
      },
      {
        id: 'wg_hospital_gap',
        title: 'Zero Hospitals Within Boundaries',
        icon: '\uD83C\uDFE5',
        zahir: {
          desc: 'West Garfield Park has zero full-service hospitals within its boundaries. 18,000 residents must leave the ward for emergency care.',
          obs: [
            'No hospital signs anywhere in the ward',
            'Empty lots where medical facilities could stand',
            'Ambulance response times longer than city average',
          ],
        },
        threeQ: {
          is: 'The absence of a hospital is itself an Ayah.',
          what: 'A healthcare void. The Huduth Dhati of a hospital is healing. Its absence is structural neglect.',
          when: 'Population peaked at 70,000 with hospitals. Population dropped 75%. Hospitals left. 18,000 remain without.',
        },
        sunnat: [
          {
            process: 'Medical Institutional Flight',
            sci: 'Population decline \u2192 hospital closure \u2192 healthcare desert \u2192 preventable death',
            attrs: [
              { ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a', en: 'Ash-Shafi', m: 'The Healer', role: 'Ash-Shafi heals. The absence of a hospital does not mean the absence of healing need.' },
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u0641\u0650\u064a\u0651', en: 'Al-Qarib', m: 'The Near One', role: 'Al-Qarib is near. The hospital is not.' },
            ],
            ayah: '"When I am ill, it is He who cures me." (26:80)',
          },
        ],
        haqq: 'Zero hospitals for 18,000 people is not a market failure — it is a moral failure.',
        amanah: 'Document the healthcare void. Advocate for community health infrastructure.',
        uboor: 'Bridges to health resources.',
        bridges: ['health'],
        catalog: 'Your hospital gap documentation maps the healthcare void.',
      },
    ],
    'back-of-the-yards': [
      {
        id: 'by_musalla',
        title: 'Archer Ave Storefront Musalla',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'A storefront musalla on Archer Ave serving Rohingya, Syrian, and Yemeni families. Shoes lined outside. The sound of Quran recitation through the open door.',
          obs: [
            'Small storefront with Arabic calligraphy',
            'Shoes outside the door',
            'Multilingual community gathered for salah',
            'Adjacent businesses: taco shop, halal butcher, laundromat',
          ],
        },
        threeQ: {
          is: 'This musalla is Wujud — existence. Community creating sacred space from commercial space.',
          what: 'A prayer space. Transformed by intention from retail to Ibadah.',
          when: 'Established as refugee communities settled. The storefront became a masjid because the community needed one.',
        },
        sunnat: [
          {
            process: 'Sacred Space Creation',
            sci: 'Refugee settlement \u2192 communal need \u2192 commercial space acquisition \u2192 spiritual transformation \u2192 community anchor',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0641\u064e\u062a\u064e\u0651\u0627\u062d', en: 'Al-Fattah', m: 'The Opener', role: 'Al-Fattah opens a door. A storefront becomes a house of Allah.' },
              { ar: '\u0627\u0644\u0633\u064e\u0651\u0644\u064e\u0627\u0645', en: 'As-Salam', m: 'The Source of Peace', role: 'As-Salam fills the space. Commerce becomes peace.' },
            ],
            ayah: '"In houses which Allah has permitted to be raised." (24:36)',
          },
        ],
        haqq: 'Sacred space is not architecture — it is intention.',
        amanah: 'Document the musalla. Bridge to community resources.',
        uboor: 'Bridges to community resources.',
        bridges: ['community'],
        catalog: 'Your musalla documentation maps sacred space creation.',
      },
    ],
  },
  atlanta: {
    'english-vine': [
      {
        id: 'iman_atl_campus',
        title: 'IMAN Atlanta — Green ReEntry Site',
        icon: '\uD83C\uDF31',
        zahir: {
          desc: 'IMAN Atlanta\'s Green ReEntry program site in the English Avenue / Vine City corridor. Construction training, transitional housing, and community health converging on one block.',
          obs: [
            'NCCER-certified construction training in progress',
            '20-bed Village transitional housing visible',
            'Community health workers in the corridor',
            'Vacant lots adjacent — potential for expansion',
          ],
        },
        threeQ: {
          is: 'IMAN Atlanta exists as proof that Muslim-led holistic development works in the American South.',
          what: 'A reentry ecosystem. The Huduth Dhati is Khidmah — service to the returning citizen.',
          when: 'English Avenue has been in crisis for decades. IMAN planted here. The Village opened. The Green ReEntry program certified its first cohort.',
        },
        sunnat: [
          {
            process: 'Reentry Ecosystem Development',
            sci: 'Returning citizen \u2192 vocational training \u2192 employment \u2192 housing stability \u2192 community reintegration',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0639\u064e\u0641\u064f\u0648\u0651', en: 'Al-Afuww', m: 'The Pardoner', role: 'Every returning citizen is met with the mercy of Al-Afuww — that return and restoration are always possible.' },
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: 'NCCER certification is Rizq — provision through skill, through hands, through the earth\'s materials.' },
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Reentry is resurrection. Al-Muhyi gives life to what the system tried to bury.' },
            ],
            ayah: '"Say: O My servants who have transgressed against themselves, do not despair of the mercy of Allah." (39:53)',
          },
        ],
        haqq: 'The returning citizen is not a liability — they are an asset. IMAN\'s model proves it.',
        amanah: 'Support IMAN\'s work. Connect returning citizens to the program. Document the transformation.',
        uboor: 'Bridges directly to Reentry and Community categories in the resource guide.',
        bridges: ['reentry', 'community', 'income'],
        catalog: 'Your IMAN Atlanta documentation enters the network of Muslim-led holistic development.',
      },
    ],
    'west-end': [
      {
        id: 'community_masjid_atl',
        title: 'Community Masjid of Atlanta — 1976',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'The Community Masjid on Cascade Ave — established 1976 by Imam W.D. Mohammed\'s community. The BeltLine Westside Trail runs nearby. Gentrification pressure is visible in every direction.',
          obs: [
            'Masjid building on Cascade Ave — maintained, active',
            'BeltLine trail visible two blocks away',
            'New construction adjacent to legacy buildings',
            'For-sale signs on neighboring properties',
          ],
        },
        threeQ: {
          is: 'This Masjid exists as Al-Baqi — the Everlasting — in a neighborhood being transformed around it.',
          what: 'An anchor institution. The Huduth Dhati is community — gathering, provision, worship, service.',
          when: 'Established 1976. The BeltLine triggered gentrification 2010-2020. The Masjid remains.',
        },
        sunnat: [
          {
            process: 'Anchor Institution Persistence',
            sci: 'Community institution established \u2192 serves through disinvestment \u2192 gentrification arrives \u2192 institution holds the line',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0628\u064e\u0627\u0642\u0650\u064a', en: 'Al-Baqi', m: 'The Everlasting', role: 'The Masjid endures when the neighborhood transforms. Al-Baqi is the attribute of what remains.' },
              { ar: '\u0627\u0644\u0652\u0648\u064e\u0644\u0650\u064a\u0651', en: 'Al-Wali', m: 'The Protecting Friend', role: 'The Masjid protects the community. Al-Wali\'s protection operates through human institutions.' },
              { ar: '\u0627\u0644\u0652\u062c\u064e\u0627\u0645\u0650\u0639', en: 'Al-Jami', m: 'The Gatherer', role: 'The Masjid gathers the community that displacement tries to scatter.' },
            ],
            ayah: '"In houses which Allah has permitted to be raised — that His name be mentioned therein." (24:36)',
          },
        ],
        haqq: 'The Masjid is the line. The community that chose to root here chose to be the roots. The Amanah is to hold.',
        amanah: 'Support the Masjid\'s anti-displacement work. Connect to Atlanta Land Trust.',
        uboor: 'Bridges to Housing and Community categories. The Masjid\'s zakat programs bridge to Food and Income.',
        bridges: ['housing', 'community', 'food'],
        catalog: 'Your Community Masjid documentation maps anchor institution persistence against gentrification.',
      },
    ],
  },
  cincinnati: {
    avondale: [
      {
        id: 'icgc_minaret',
        title: 'Islamic Center of Greater Cincinnati',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'The Islamic Center of Greater Cincinnati on Reading Road — one of Ohio\'s earliest masajid, serving since the 1970s. Next to it, Children\'s Hospital gleams with billion-dollar investment.',
          obs: [
            'Masjid building with community programming signs',
            'Children\'s Hospital campus expanding next door',
            'Reading Road commercial corridor — mixed occupancy',
            'Families from multiple cultural backgrounds arriving',
            'Medical apartheid visible: world-class care adjacent to underserved community',
          ],
        },
        threeQ: {
          is: 'This Masjid exists as anchor, witness, and bridge. It has watched Reading Road transform for 50 years. It serves the community that the adjacent hospital\'s investment bypasses.',
          what: 'The juxtaposition is the Ayah: a billion-dollar medical campus next to a community with 22% uninsured. The Islamic Center bridges where the institution does not.',
          when: 'Established in the 1970s. Children\'s Hospital has expanded repeatedly. Community health indicators have not improved proportionally.',
        },
        sunnat: [
          {
            process: 'Institutional Adjacency Paradox',
            sci: 'Major institution investment >> route through Black neighborhood >> demolition >> displacement >> generational wealth loss',
            attrs: [
              { ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a', en: 'Al-Haqq', m: 'The True Reality', role: 'The route was chosen to destroy Black wealth.' },
              { ar: '\u0627\u0644\u0631\u064e\u0651\u0642\u0650\u064a\u0628', en: 'Ar-Raqib', m: 'The Watchful One', role: 'Ar-Raqib watches over all transactions. Every lease, every eviction, every tax credit allocation. The Caretaker\'s documentation is an act of divine watchfulness.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0631\u0650\u064a\u0628', en: 'Al-Qarib', m: 'The Near One', role: 'Al-Qarib is near to all. The hospital is physically near but institutionally distant from the community. The Masjid closes that distance.' },
            ],
            ayah: '"And when My servants ask you concerning Me, indeed I am near. I respond to the invocation of every supplicant when they call upon Me." (2:186)',
          },
        ],
        haqq: 'Proximity is not access. The hospital and the community share a street but not outcomes. The Masjid works to close the gap.',
        amanah: 'Document the adjacency paradox. Measure who the institutional investment actually serves. Bridge the gap through direct service and advocacy.',
        uboor: 'Bridges to health, community, and education resources in Avondale.',
        bridges: ['health', 'community', 'edu'],
        catalog: 'Your documentation of this institutional-community dynamic adds to the Avondale equity record.',
      },
    ],
    'over-the-rhine': [
      {
        id: 'otr_facade',
        title: 'Over-the-Rhine Storefront',
        icon: '\uD83C\uDFDB',
        zahir: {
          desc: 'A renovated Italianate building on Vine Street in Over-the-Rhine. Craft cocktail bar at street level. The apartment above rents for $2,400/month. In 2001, this block was 90% Black and low-income.',
          obs: [
            'Restored 19th-century Italianate facade',
            'Upscale restaurant with outdoor seating',
            '\'Luxury Living\' rental signs in windows',
            'No visible community-serving institutions on the block',
            'Findlay Market two blocks north — increasingly tourist-oriented',
          ],
        },
        threeQ: {
          is: 'This facade exists as beauty and erasure simultaneously. The architecture was preserved. The community was not.',
          what: 'The Huduth Dhati of this building is shelter and commerce. Its Huduth Zamani: built 1870s by German immigrants, home to Black and Appalachian families by 1980s, luxury condos by 2015.',
          when: '2001: Timothy Thomas killed by police. Civil unrest. 2003: 3CDC formed to \'revitalize.\' 2024: Average rent exceeds community median income.',
        },
        sunnat: [
          {
            process: 'Gentrification Architecture',
            sci: 'Historic preservation tax credits >> renovation investment >> luxury conversion >> original residents priced out >> cultural erasure with aesthetic preservation',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Residents climb daily with patience what the incline once carried.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Strength to climb what was once carried. Physical metaphor for community resilience.' },
            ],
            ayah: '"Indeed, with hardship will be ease." (94:6)',
          },
        ],
        haqq: 'Beautiful buildings and displaced communities. The Haqq is that revitalization without equity is extraction with better aesthetics.',
        amanah: 'Document who lives here now versus who lived here in 2000. The demographic shift is an Ayah of structural Zulm dressed in historic preservation.',
        uboor: 'Bridges to displacement resources, housing advocacy, and community organizations fighting for affordable housing in OTR.',
        bridges: ['housing', 'community'],
        catalog: 'Your documentation of this storefront\'s transformation enters the displacement record of Over-the-Rhine.',
      },
    ],
    'price-hill': [
      {
        id: 'prichill_musalla',
        title: 'Price Hill Storefront Musalla',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'A storefront prayer space on Warsaw Ave. Somali families gather for Dhuhr. Recovery center next door.',
          obs: [
            'Small storefront with Arabic calligraphy',
            'Shoes lined outside',
            'Recovery center next door',
            'Appalachian and immigrant families sharing the block',
          ],
        },
        threeQ: {
          is: 'Refugee resilience meets opioid devastation on one block.',
          what: 'A prayer space in a neighborhood of intersecting crises.',
          when: 'Storefront opened 2010s. Opioid crisis peaked 2016-2020. Both present tense.',
        },
        sunnat: [
          {
            process: 'Crisis Intersection',
            sci: 'Opioid crisis >> property devaluation >> refugee placement >> coexistence',
            attrs: [
              { ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a', en: 'Ash-Shafi', m: 'The Healer', role: 'The musalla and recovery center sit side by side — two forms of healing.' },
              { ar: '\u0627\u0644\u0631\u064e\u0651\u0624\u064f\u0648\u0641', en: 'Ar-Rauf', m: 'The Compassionate', role: 'Communities in different crises extending grace to each other.' },
            ],
            ayah: '"Allah does not burden a soul beyond that it can bear." (2:286)',
          },
        ],
        haqq: 'Two communities in crisis sharing space with dignity.',
        amanah: 'Document the intersection. Bridge communities to shared resources.',
        uboor: 'Bridges to health and community.',
        bridges: ['health', 'community'],
        catalog: 'Your Price Hill documentation maps crisis intersection.',
      },
      {
        id: 'prichill_stairs',
        title: 'Price Hill Incline Stairs',
        icon: '\u25B2',
        zahir: {
          desc: 'The steep stairs where the Price Hill Incline once ran — a funicular that connected hilltop residents to the city below. Now just concrete steps.',
          obs: [
            'Steep concrete stairs climbing the hill',
            'View of Cincinnati basin below',
            'Residents climbing daily what others drive',
            'Historical marker for the incline railway',
          ],
        },
        threeQ: {
          is: 'These stairs are an Ayah of infrastructure loss.',
          what: 'What remains when public transit is removed.',
          when: 'Incline operated 1870s-1940s. Closed. Stairs remain. Residents still climb.',
        },
        sunnat: [
          {
            process: 'Transit Infrastructure Loss',
            sci: 'Public transit >> ridership >> cost pressure >> closure >> stairs remain >> daily climb',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Residents climb daily with patience what the incline once carried.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Strength to climb what was once carried. Physical metaphor for community resilience.' },
            ],
            ayah: '"Indeed, with hardship will be ease." (94:6)',
          },
        ],
        haqq: 'The stairs are what remains when investment is withdrawn.',
        amanah: 'Document the daily climb. Advocate for transit restoration.',
        uboor: 'Bridges to community resources.',
        bridges: ['community'],
        catalog: 'Your incline stairs documentation maps transit loss.',
      },
    ],
    evanston: [
      {
        id: 'evanston_youth',
        title: 'Evanston Youth Center',
        icon: '\uD83C\uDFC0',
        zahir: {
          desc: 'Youth center on Hewitt Ave. After-school programs where 45% of children live below poverty.',
          obs: [
            'Children playing basketball',
            'Tutoring tables with homework',
            'Snack table — afternoon meal for many',
            'Staff with deep community roots',
          ],
        },
        threeQ: {
          is: 'In a ward where 45% of children are poor, this space is Rizq.',
          what: 'A gathering place for the most vulnerable.',
          when: 'Established through community advocacy. Operates daily.',
        },
        sunnat: [
          {
            process: 'Youth Development Ecology',
            sci: 'Safe space >> nutrition >> academic support >> mentoring >> opportunity',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0648\u064e\u0643\u0650\u064a\u0644', en: 'Al-Wakil', m: 'The Trustee', role: 'Al-Wakil entrusts youth to community. This center fulfills that trust.' },
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u0641\u0650\u0638', en: 'Al-Hafiz', m: 'The Preserver', role: 'Preserves childhood where childhood is under threat.' },
            ],
            ayah: '"Let those fear who left behind weak offspring." (4:9)',
          },
        ],
        haqq: 'Every child here is an Amanah.',
        amanah: 'Document youth programs. Strengthen the net.',
        uboor: 'Bridges to youth and education.',
        bridges: ['youth', 'edu'],
        catalog: 'Your youth center documentation joins child welfare map.',
      },
    ],
  },
  detroit: {
    'east-side': [
      {
        id: 'vacant_prairie',
        title: 'East Side Urban Prairie',
        icon: '\uD83C\uDF3E',
        zahir: {
          desc: 'An entire block on Gratiot where houses once stood. Tallgrass, wildflowers, and a returning ecosystem.',
          obs: [
            'Prairie grass 4 feet tall on former lots',
            'Foundation outlines beneath vegetation',
            'A hawk hunting from a utility pole',
            'Masjid Wali Muhammad three blocks away',
          ],
        },
        threeQ: {
          is: 'Nature reclaiming human abandonment. Simultaneously loss and renewal.',
          what: 'An ecosystem where houses stood.',
          when: 'Houses demolished 2000s. Prairie established within 5 years.',
        },
        sunnat: [
          {
            process: 'Ecological Succession',
            sci: 'Demolition >> rubble >> pioneer species >> grassland >> complexity',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Al-Muhyi gives life where humans withdrew.' },
              { ar: '\u0627\u0644\u0652\u0628\u064e\u0627\u0631\u0650\u0626', en: 'Al-Bari', m: 'The Evolver', role: 'Within 10 years, demolished block becomes functioning prairie.' },
            ],
            ayah: '"You see the earth barren, but when We send down rain, it quivers and swells." (22:5)',
          },
        ],
        haqq: 'Nature does not wait for policy.',
        amanah: 'Document the ecosystem. Map what grows where houses stood.',
        uboor: 'Bridges to food and community — urban farming potential.',
        bridges: ['food', 'community'],
        catalog: 'Your prairie documentation maps ecological resurrection.',
      },
      {
        id: 'gratiot_masjid',
        title: 'Masjid Wali Muhammad',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'Masjid on Gratiot Ave — W.D. Mohammed community serving amid 45% vacancy.',
          obs: [
            'Masjid building amid vacant lots',
            'Friday congregants arriving',
            'Community bulletin board',
            '45% vacancy surrounding — but the masjid is full',
          ],
        },
        threeQ: {
          is: 'A masjid named The Patient in the displacement zone. The name is the Ayah.',
          what: 'A community anchor for displaced Muslim families.',
          when: 'Established as East Portland\'s Muslim population grew through displacement and immigration.',
        },
        sunnat: [
          {
            process: 'Anchor in Displacement Zone',
            sci: 'Displacement >> Muslim families concentrated >> need for community space >> masjid established >> services grow',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'The masjid is named for the attribute its community practices daily.' },
              { ar: '\u0627\u0644\u0652\u062c\u064e\u0627\u0645\u0650\u0639', en: 'Al-Jami', m: 'The Gatherer', role: 'Gathers families scattered by displacement from across the city and across the world.' },
            ],
            ayah: '"Indeed, Allah is with the patient." (2:153)',
          },
        ],
        haqq: 'Masjid As-Saber: patience as practice, gathering as provision.',
        amanah: 'Document the community. Bridge to resources.',
        uboor: 'Bridges to community and health.',
        bridges: ['community', 'health'],
        catalog: 'Your governance documentation enters the civic engagement record.',
      },
    ],
    hamtramck: [
      {
        id: 'hamtramck_council',
        title: 'Hamtramck City Hall',
        icon: '\uD83C\uDFDB',
        zahir: {
          desc: 'Seat of the first all-Muslim city council in American history (2021).',
          obs: [
            'City Hall with municipal flags',
            'Multilingual signage',
            'Halal restaurants visible from steps',
            'Community members entering for public meetings',
          ],
        },
        threeQ: {
          is: 'Muslim civic participation reaching governance.',
          what: 'Civic authority exercised by Muslim citizens.',
          when: 'Muslim families settled 1990s. Majority-Muslim by 2010s. All-Muslim council 2021.',
        },
        sunnat: [
          {
            process: 'Civic Progression',
            sci: 'Immigration >> community building >> civic engagement >> governance',
            attrs: [
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0643\u064e\u0645', en: 'Al-Hakam', m: 'The Judge', role: 'Will governance reflect divine justice?' },
              { ar: '\u0627\u0644\u0652\u0645\u064e\u0644\u0650\u0643', en: 'Al-Malik', m: 'The Sovereign', role: 'Human governance is Amanah, not ownership.' },
            ],
            ayah: '"Allah commands you to render trusts to whom they are due." (4:58)',
          },
        ],
        haqq: 'Muslim governance is not an end but a means — the test is justice for ALL residents.',
        amanah: 'Document how Muslim governance addresses community needs.',
        uboor: 'Bridges to community and health.',
        bridges: ['community', 'health'],
        catalog: 'Your governance documentation enters the civic engagement record.',
      },
      {
        id: 'jos_campau',
        title: 'Jos. Campau Halal Corridor',
        icon: '\uD83E\uDD57',
        zahir: {
          desc: 'Most concentrated halal food corridor in the Midwest. 15+ markets in 2 square miles.',
          obs: [
            '15+ halal markets, butchers, restaurants',
            'Arabic, Bengali, Bosnian signage',
            'Families shopping for Ramadan provisions',
            'Shawarma and biryani mixing on the sidewalk',
          ],
        },
        threeQ: {
          is: 'Rizq made manifest through community enterprise.',
          what: 'A food ecosystem. Provision through self-sufficiency.',
          when: 'Businesses established 1990s-present. Grew organically with community.',
        },
        sunnat: [
          {
            process: 'Community Food Sovereignty',
            sci: 'Immigrant community >> halal demand >> entrepreneurship >> supply chain >> self-sufficient corridor',
            attrs: [
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: 'Every halal market is a channel of divine provision.' },
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0627\u062d', en: 'Al-Fattah', m: 'The Opener', role: 'Each storefront is a door opened.' },
            ],
            ayah: '"In the earth are neighboring tracts and gardens." (13:4)',
          },
        ],
        haqq: 'Communities can build their own food sovereignty.',
        amanah: 'Document the corridor as model. What conditions enabled this?',
        uboor: 'Bridges to food and income.',
        bridges: ['food', 'income'],
        catalog: 'Your Jos. Campau documentation maps halal food sovereignty.',
      },
    ],
    brightmoor: [
      {
        id: 'brightmoor_farm',
        title: 'Brightmoor Farmway',
        icon: '\uD83C\uDF3D',
        zahir: {
          desc: '10 urban farms on formerly vacant lots. Food grows where houses stood.',
          obs: [
            'Raised beds on former house lots',
            'Hoop house extending growing season',
            'Neighbors harvesting together',
            '42% vacancy becomes agricultural potential',
          ],
        },
        threeQ: {
          is: 'Islah made visible. Where vacancy dominated, Caretakers planted.',
          what: 'Urban agriculture on abandoned land.',
          when: 'Corridor collapsed 1970s-2000s. Farms established 2010s.',
        },
        sunnat: [
          {
            process: 'Urban Agricultural Succession',
            sci: 'Demolition >> vacant lot >> soil remediation >> raised beds >> cultivation >> food production',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Al-Muhyi commands life from abandoned lots.' },
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: '80% food desert. Yet food grows here.' },
            ],
            ayah: '"If the Hour is established and one has a palm shoot, let him plant it." (Ahmad)',
          },
        ],
        haqq: '42% vacancy is 42% agricultural potential.',
        amanah: 'Document farms. Map yields.',
        uboor: 'Bridges to food and community.',
        bridges: ['food', 'community'],
        catalog: 'Your Brightmoor farm documentation maps urban food sovereignty.',
      },
    ],
  },
  louisville: {
    'west-louisville': [
      {
        id: '9th_street',
        title: 'The 9th Street Divide',
        icon: '\u2502',
        zahir: {
          desc: 'Standing at 9th Street looking west into Russell and east toward downtown. Two Louisvilles separated by one street.',
          obs: [
            'Downtown skyscrapers to the east',
            'Russell neighborhood stretching west — vacancy, dollar stores',
            'Physical line: road width changes, sidewalk condition changes',
            'Life expectancy gap: 10+ years across this street',
          ],
        },
        threeQ: {
          is: 'One street. Two life expectancies.',
          what: 'A boundary. Segregation codified in 1914.',
          when: 'Louisville passed residential segregation ordinance 1914. Supreme Court struck it down 1917. But 9th Street remains the line.',
        },
        sunnat: [
          {
            process: 'Structural Segregation',
            sci: 'Ordinance >> redlining >> disinvestment >> health disparity >> generational poverty',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0639\u064e\u062f\u0652\u0644', en: 'Al-Adl', m: 'The Just', role: 'Life expectancy should not depend on which side of 9th Street you were born.' },
              { ar: '\u0627\u0644\u0652\u0645\u064f\u0642\u0652\u0633\u0650\u0637', en: 'Al-Muqsit', m: 'The Equitable', role: '110 years of inequity require structural remedy.' },
            ],
            ayah: '"Allah commands justice and good conduct." (16:90)',
          },
        ],
        haqq: 'Textbook for the Three Questions: it is, it is segregation, it was created by ordinance in 1914.',
        amanah: 'Document both sides. Measure the gap.',
        uboor: 'Bridges to all resource categories.',
        bridges: ['health', 'housing', 'food'],
        catalog: 'Your 9th Street documentation maps the physical geography of structural racism.',
      },
      {
        id: 'breonna_site',
        title: 'Springfield Drive Memorial',
        icon: '\uD83D\uDC9C',
        zahir: {
          desc: 'Apartment complex where Breonna Taylor was killed March 13, 2020. Community memorials mark the site.',
          obs: [
            'Apartment building with memorial flowers',
            'West Louisville stretching in every direction',
            'Her death catalyzed national reckoning',
            'The context of her life is this ward',
          ],
        },
        threeQ: {
          is: 'Memorial, crime scene, catalyst, and Ayah.',
          what: 'A home that became a monument. Safety violated.',
          when: 'March 13, 2020. Conditions that led to it: decades of over-policing and under-investment.',
        },
        sunnat: [
          {
            process: 'Systemic Violence',
            sci: 'Structural racism >> over-policing >> no-knock warrants >> fatal encounter >> reckoning',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0639\u064e\u062f\u0652\u0644', en: 'Al-Adl', m: 'The Just', role: 'Al-Adl commands justice. The police state violates it.' },
              { ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a', en: 'Ash-Shafi', m: 'The Healer', role: 'The community needs healing. The police state prevents it.' },
            ],
            ayah: '"Whoever saves a life, it is as if they saved all of humanity." (5:32)',
          },
        ],
        haqq: 'Her death exposed what West Louisville has known for generations.',
        amanah: 'Bear witness. Document.',
        uboor: 'Bridges to community and health.',
        bridges: ['community', 'health'],
        catalog: 'Your documentation bears witness.',
      },
    ],
    iroquois: [
      {
        id: 'bosnian_center',
        title: 'Bosnian Islamic Center',
        icon: '\uD83D\uDD4C',
        zahir: {
          desc: 'Bosnian Islamic Center in Iroquois area — serving refugees from Bosnian War, now welcoming Somali, Syrian families.',
          obs: [
            'Masjid with Bosnian architectural touches',
            'Multilingual congregation',
            'Community garden with diverse vegetables',
            'Children speaking three or four languages',
          ],
        },
        threeQ: {
          is: 'Refugees from different wars, speaking different languages, praying in the same direction.',
          what: 'Sanctuary. A gathering place for the displaced.',
          when: 'Bosnian families mid-1990s. Somali 2000s. Syrian 2010s. Each wave adds.',
        },
        sunnat: [
          {
            process: 'Refugee Community Building',
            sci: 'War displacement >> resettlement >> community formation >> institution building',
            attrs: [
              { ar: '\u0627\u0644\u0652\u062c\u064e\u0627\u0645\u0650\u0639', en: 'Al-Jami', m: 'The Gatherer', role: 'Al-Jami gathers those scattered by war.' },
              { ar: '\u0627\u0644\u0652\u0645\u064f\u0624\u0652\u0645\u0650\u0646', en: 'Al-Mu\'min', m: 'The Source of Security', role: 'After war, the center provides security of belonging.' },
            ],
            ayah: '"Enter into peace completely." (2:208)',
          },
        ],
        haqq: 'Refugees are not victims — they are builders.',
        amanah: 'Document community-building model. Bridge to resources for newest arrivals.',
        uboor: 'Bridges to community and health.',
        bridges: ['community', 'health'],
        catalog: 'Your documentation maps refugee community building.',
      },
      {
        id: 'iroquois_park',
        title: 'Iroquois Park Overlook',
        icon: '\uD83C\uDF33',
        zahir: {
          desc: 'Iroquois Park — an Olmsted-designed park offering panoramic views of Louisville. The most ethnically diverse neighborhood in Kentucky surrounds it.',
          obs: [
            'Panoramic view of Louisville basin',
            'Diverse families using the park: Somali, Bosnian, Bhutanese, AA',
            'Old-growth trees in Olmsted design',
            'The natural and human diversity coexist',
          ],
        },
        threeQ: {
          is: 'A park. The Huduth Dhati is commons — shared public space.',
          what: 'A park. The Huduth Dhati is commons — shared public space.',
          when: 'Designed 1890s by Olmsted. Refugee communities settled nearby 1990s-2010s.',
        },
        sunnat: [
          {
            process: 'Commons as Meeting Ground',
            sci: 'Public park design >> shared space >> diverse community use >> intercultural encounter',
            attrs: [
              { ar: '\u0627\u0644\u0652\u062c\u064e\u0627\u0645\u0650\u0639', en: 'Al-Jami', m: 'The Gatherer', role: 'The park gathers what the city separates.' },
              { ar: '\u0627\u0644\u0652\u062c\u064e\u0645\u0650\u064a\u0644', en: 'Al-Jamil', m: 'The Beautiful', role: 'Beauty in the park and beauty in the diversity of those who use it.' },
            ],
            ayah: '"And among His signs is the creation of the heavens and the earth and the diversity of your languages and colors." (30:22)',
          },
        ],
        haqq: 'The park is commons. Everyone belongs here regardless of origin.',
        amanah: 'Document the intercultural commons. Map how diverse communities use shared space.',
        uboor: 'Bridges to community resources.',
        bridges: ['community'],
        catalog: 'Your park documentation maps the intercultural commons.',
      },
    ],
  },
  durham: {
    hayti: [
      {
        id: 'hayti_freeway',
        title: 'Durham Freeway at Fayetteville St',
        icon: '\uD83D\uDEE3',
        zahir: {
          desc: 'Durham Freeway (NC 147) through what was Hayti — Black Wall Street of the South.',
          obs: [
            'Freeway trench through former neighborhood',
            'Ar-Razzaq Islamic Center on S. Alston',
            'NCCU campus on the hill',
            '600+ structures demolished for this road',
          ],
        },
        threeQ: {
          is: 'Infrastructure violence. Transportation and demolition simultaneously.',
          what: 'A road built through the most prosperous Black commercial district in the South.',
          when: 'NC Mutual founded 1898. 200+ businesses by 1950s. Freeway 1960s. 600+ structures demolished.',
        },
        sunnat: [
          {
            process: 'Urban Renewal as Erasure',
            sci: 'Federal highway funding >> route through Black neighborhood >> demolition >> displacement >> generational wealth loss',
            attrs: [
              { ar: '\u0627\u0644\u0634\u064e\u0651\u0627\u0641\u0650\u064a', en: 'Al-Haqq', m: 'The True Reality', role: 'The route was chosen to destroy Black wealth.' },
              { ar: '\u0627\u0644\u0631\u064e\u0651\u0642\u0650\u064a\u0628', en: 'Ar-Raqib', m: 'The Watchful One', role: 'Ar-Raqib watches over all transactions. Every lease, every eviction, every tax credit allocation. The Caretaker\'s documentation is an act of divine watchfulness.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0631\u0650\u064a\u0628', en: 'Al-Qarib', m: 'The Near One', role: 'Al-Qarib is near to all. The hospital is physically near but institutionally distant from the community. The Masjid closes that distance.' },
            ],
            ayah: '"And when My servants ask you concerning Me, indeed I am near. I respond to the invocation of every supplicant when they call upon Me." (2:186)',
          },
        ],
        haqq: 'Proximity is not access. The hospital and the community share a street but not outcomes. The Masjid works to close the gap.',
        amanah: 'Document the adjacency paradox. Measure who the institutional investment actually serves. Bridge the gap through direct service and advocacy.',
        uboor: 'Bridges to health, community, and education resources in Avondale.',
        bridges: ['health', 'community', 'edu'],
        catalog: 'Your documentation of this institutional-community dynamic adds to the Avondale equity record.',
      },
      {
        id: 'nccu_walk',
        title: 'NCCU Campus Walk',
        icon: '\uD83C\uDFEB',
        zahir: {
          desc: 'NC Central University — HBCU founded 1910, one of the first for Black Americans in the South.',
          obs: [
            'Historic campus buildings from early 1900s',
            'Students crossing Fayetteville Street',
            'Research buildings adjacent to Hayti',
            'University endures where commercial district was destroyed',
          ],
        },
        threeQ: {
          is: 'Educational self-determination. Founded because the system excluded.',
          what: 'An HBCU. Education as liberation.',
          when: 'Founded 1910. Survived segregation, depression, integration pressures. Still standing.',
        },
        sunnat: [
          {
            process: 'Educational Persistence',
            sci: 'Exclusion >> self-determination >> institution building >> generational education',
            attrs: [
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: 'Provision through educational self-determination.' },
              { ar: '\u0627\u0644\u0631\u064e\u0651\u0624\u064f\u0648\u0641', en: 'Ar-Rauf', m: 'The Compassionate', role: 'Communities in different crises extending grace to each other.' },
            ],
            ayah: '"Allah does not burden a soul beyond that it can bear." (2:286)',
          },
        ],
        haqq: 'Communities build what systems deny.',
        amanah: 'Document the institution as community anchor.',
        uboor: 'Bridges to education and community.',
        bridges: ['edu', 'community'],
        catalog: 'Your documentation of this educational institution adds to the HBCU equity record.',
      },
    ],
    'east-durham': [
      {
        id: 'angier_corridor',
        title: 'Angier Ave Immigrant Corridor',
        icon: '\uD83C\uDFEA',
        zahir: {
          desc: 'Angier Ave through East Durham — the Triangle\u2019s immigrant and refugee corridor.',
          obs: [
            'Tiendas, halal markets, and Burmese restaurants',
            'Multilingual signage',
            'Families navigating without documentation',
            '22% uninsured — highest in the study',
          ],
        },
        threeQ: {
          is: 'Where immigrants build new lives with minimal institutional support.',
          what: 'An immigrant corridor. Survival and entrepreneurship.',
          when: 'Latino families established 1990s-2000s. Burmese, Somali, Bhutanese followed.',
        },
        sunnat: [
          {
            process: 'Immigrant Corridor Formation',
            sci: 'Immigration >> settlement in affordable area >> ethnic business >> corridor identity >> community',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Residents climb daily with patience what the incline once carried.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Strength to climb what was once carried. Physical metaphor for community resilience.' },
            ],
            ayah: '"Indeed, with hardship will be ease." (94:6)',
          },
        ],
        haqq: 'Immigrant communities build infrastructure the city does not provide.',
        amanah: 'Document the corridor. Bridge to health and education resources.',
        uboor: 'Bridges to health and education.',
        bridges: ['health', 'edu'],
        catalog: 'Your documentation of this immigrant corridor adds to the Triangle equity record.',
      },
    ],
  },
  portland: {
    northeast: [
      {
        id: 'alberta_st',
        title: 'Alberta Street Arts District',
        icon: '\uD83C\uDFA8',
        zahir: {
          desc: 'Alberta Street — once Black Portland\'s spine. Now arts district. Black population dropped from 75% to under 25%.',
          obs: [
            'Art galleries and craft beer bars',
            'Last Thursday crowds — mostly white',
            'Few remaining Black-owned businesses with legacy labels',
            '$800K houses that sold for $80K in 2000',
          ],
        },
        threeQ: {
          is: 'Cultural extraction — aesthetic of a Black neighborhood marketed without the community.',
          what: 'An arts district built on displacement.',
          when: 'Post-Vanport 1948. Cultural peak 1970s-90s. Gentrification 2000s. By 2020, near-total.',
        },
        sunnat: [
          {
            process: 'Cultural Displacement',
            sci: 'Black cultural creation >> neighborhood identity >> development >> property rise >> community priced out >> culture commodified',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Residents climb daily with patience what the incline once carried.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Strength to climb what was once carried. Physical metaphor for community resilience.' },
            ],
            ayah: '"Indeed, with hardship will be ease." (94:6)',
          },
        ],
        haqq: 'Beautiful buildings and displaced communities. The Haqq is that revitalization without equity is extraction with better aesthetics.',
        amanah: 'Document who lives here now versus who lived here in 2000. The demographic shift is an Ayah of structural Zulm dressed in historic preservation.',
        uboor: 'Bridges to displacement resources, housing advocacy, and community organizations fighting for affordable housing in OTR.',
        bridges: ['housing', 'community'],
        catalog: 'Your documentation of this storefront\'s transformation enters the displacement record of Over-the-Rhine.',
      },
    ],
    'east-portland': [
      {
        id: '82nd_corridor',
        title: '82nd Avenue Corridor',
        icon: '\uD83D\uDEE3',
        zahir: {
          desc: '82nd Ave through East Portland — displacement receiving zone. Halal markets, taco trucks, Somali restaurants.',
          obs: [
            'Oregon\'s most dangerous pedestrian road',
            'Halal market next to taco truck next to pho restaurant',
            'Apartment complexes where displaced families concentrate',
            'No sidewalks in some stretches',
          ],
        },
        threeQ: {
          is: 'Where displaced families end up. Furthest from center, least investment, most need.',
          what: 'A displacement terminus.',
          when: 'Became displacement destination 2000s-2010s as inner Portland gentrified.',
        },
        sunnat: [
          {
            process: 'Displacement Concentration',
            sci: 'Inner gentrification >> family displacement >> concentration in low-cost area >> underinvestment',
            attrs: [
              { ar: '\u0627\u0644\u0635\u064e\u0651\u0628\u064f\u0648\u0631', en: 'As-Sabur', m: 'The Patient', role: 'Residents climb daily with patience what the incline once carried.' },
              { ar: '\u0627\u0644\u0652\u0642\u064e\u0648\u0650\u064a\u0651', en: 'Al-Qawiyy', m: 'The Strong', role: 'Strength to climb what was once carried. Physical metaphor for community resilience.' },
            ],
            ayah: '"Indeed, with hardship will be ease." (94:6)',
          },
        ],
        haqq: 'Nature does not wait for policy.',
        amanah: 'Document who lives here now versus who lived here in 2000. The demographic shift is an Ayah of structural Zulm dressed in historic preservation.',
        uboor: 'Bridges to displacement resources, housing advocacy, and community organizations fighting for affordable housing in OTR.',
        bridges: ['housing', 'community'],
        catalog: 'Your documentation of this storefront\'s transformation enters the displacement record of Over-the-Rhine.',
      },
    ],
  },
  pittsburgh: {
    'hill-district': [
      {
        id: 'civic_void',
        title: 'Lower Hill Redevelopment Site',
        icon: '\u25A1',
        zahir: {
          desc: '28-acre void where Civic Arena stood. Before arena: 8,000+ residents, 400+ businesses.',
          obs: [
            '28 acres cleared land in city center',
            'Hill District residential streets uphill',
            'Development renderings on construction fencing',
            'Community protest signs demanding benefits',
          ],
        },
        threeQ: {
          is: 'The longest displacement in Pittsburgh history. 60+ years unresolved.',
          what: 'A void. Absence with a history.',
          when: 'Demolished 1956-1961. Arena built. Arena demolished 2012. Site still partially empty.',
        },
        sunnat: [
          {
            process: 'Generational Displacement Void',
            sci: 'Demolition >> arena >> obsolescence >> demolition >> proposals >> community demands >> unresolved',
            attrs: [
              { ar: '\u0627\u0644\u0652\u062d\u064e\u0642\u0651', en: 'Al-Haqq', m: 'The True Reality', role: '8,000 people lived here. Removed. 60+ years later, justice pending.' },
              { ar: '\u0627\u0644\u0652\u0639\u064e\u062f\u0652\u0644', en: 'Al-Adl', m: 'The Just', role: 'Community benefit agreements. Affordable housing. Economic inclusion.' },
            ],
            ayah: '"Allah commands you to render trusts to whom they are due." (4:58)',
          },
        ],
        haqq: 'The void is full of unresolved injustice.',
        amanah: 'Document the void. Advocate for community-controlled development.',
        uboor: 'Bridges to displacement and housing.',
        bridges: ['housing', 'community'],
        catalog: 'Your documentation joins 60+ years of Hill District advocacy.',
      },
    ],
    homewood: [
      {
        id: 'homewood_farms',
        title: 'Homewood Urban Farm Lots',
        icon: '\uD83C\uDF3D',
        zahir: {
          desc: 'Urban farms on formerly vacant lots along Homewood Ave.',
          obs: [
            'Raised beds on former house lots',
            'Hoop house extending growing season',
            'Neighbors harvesting together',
            '42% vacancy becomes agricultural potential',
          ],
        },
        threeQ: {
          is: 'Islah made visible. Where vacancy dominated, Caretakers planted.',
          what: 'Urban agriculture on abandoned land.',
          when: 'Corridor collapsed 1970s-2000s. Farms established 2010s.',
        },
        sunnat: [
          {
            process: 'Urban Agricultural Succession',
            sci: 'Demolition >> vacant lot >> soil remediation >> raised beds >> cultivation >> food production',
            attrs: [
              { ar: '\u0627\u0644\u0652\u0645\u064f\u062d\u0652\u064a\u0650\u064a', en: 'Al-Muhyi', m: 'The Life-Giver', role: 'Al-Muhyi commands life from abandoned lots.' },
              { ar: '\u0627\u0644\u0631\u064e\u0632\u064e\u0651\u0627\u0642', en: 'Ar-Razzaq', m: 'The All-Provider', role: '80% food desert. Yet food grows here.' },
            ],
            ayah: '"If the Hour is established and one has a palm shoot, let him plant it." (Ahmad)',
          },
        ],
        haqq: '42% vacancy is 42% agricultural potential.',
        amanah: 'Document farms. Map yields.',
        uboor: 'Bridges to food and community.',
        bridges: ['food', 'community'],
        catalog: 'Your Brightmoor farm documentation maps urban food sovereignty.',
      },
    ],
  },
};

// ─── SUBMISSION UTILITIES ─────────────────────────────────────────────────────

const SUBMISSIONS_KEY = 'haal_caretaker_submissions';

export function getSubmissions(): CaretakerSubmission[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(SUBMISSIONS_KEY) || '[]');
  } catch {
    return [];
  }
}

export function saveSubmission(s: CaretakerSubmission): CaretakerSubmission[] {
  const all = getSubmissions();
  all.push(s);
  try {
    localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(all));
  } catch {
    // storage full or unavailable
  }
  return all;
}

export interface EcoTile {
  id: string;
  title: string;
  ward: string;
  city: string;
  cat: string;
  type: 'eco';
  biome: string;
  names: string[];
  bg: string;
  ayah: string;
  desc: string;
  kkh_sl: string;
  kkh_ed: string;
  kkh_o: string;
  kkh_a: string;
  bridges: string[];
  bridge_labels: string[];
}

export interface TemporalStage {
  k: string;
  ar: string;
  d: string;
  ex: string;
}

export interface BiomeData {
  id: string;
  name: string;
  ar: string;
}
